/**
 * Root router.
 *
 * Two route bundles live under this shell:
 *   - Volunteer surfaces: `/sign-in` (the standard public email-OTP
 *     flow) and `/me` (the self-service dashboard, which absorbs
 *     intake, profile edit, and the inline policy acknowledgement
 *     flow — there is no longer a separate `/ack/:token` surface;
 *     M13 collapsed it into `/me` with a per-signing email step-up
 *     code).
 *   - Admin SPA: `/admin/sign-in` + `/admin/*`.
 */

import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { SignInPage } from './routes/sign-in.js';
import { MePage } from './routes/me.js';
import { AdminSignInPage } from './routes/admin/sign-in.js';
import { AdminDocumentsPage } from './routes/admin/documents.js';
import { AdminHomePage } from './routes/admin/admin-home.js';
import { AdminExportsPage } from './routes/admin/exports.js';
import { ComplianceRequirementDetailPage } from './routes/admin/compliance-requirement-detail.js';
import { ComplianceRequirementsListPage } from './routes/admin/compliance-requirements-list.js';
import { ComplianceRecordDetailPage } from './routes/admin/compliance-record-detail.js';
import { ComplianceRecordsListPage } from './routes/admin/compliance-records-list.js';
import { PeopleListPage } from './routes/admin/people-list.js';
import { PersonDetailPage } from './routes/admin/person-detail.js';
import { PersonNewPage } from './routes/admin/person-new.js';
import { AdminUserDetailPage } from './routes/admin/user-detail.js';
import { AdminUsersListPage } from './routes/admin/users-list.js';
import { WorkerJobsPage } from './routes/admin/worker-jobs.js';
import { apiClient } from './api/client.js';

function PlaceholderPage({ title, summary }: { title: string; summary: string }): JSX.Element {
  return (
    <main className="placeholder-page">
      <h1>{title}</h1>
      <p>{summary}</p>
    </main>
  );
}

function RedirectToObligation(): JSX.Element {
  const { requirementId } = useParams<{ requirementId: string }>();
  return <Navigate to={`/admin/compliance-obligations/${requirementId ?? ''}`} replace />;
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* Volunteer surfaces */}
        <Route path="/sign-in" element={<SignInPage client={apiClient} />} />
        <Route path="/me" element={<MePage client={apiClient} />} />
        {/* /me/profile/edit was retired — the inline EditProfilePanel on
            /me replaces it. Old links land on /me via the catch-all. */}
        <Route path="/me/profile/edit" element={<Navigate to="/me" replace />} />

        {/* Admin SPA (M13) */}
        <Route path="/admin/sign-in" element={<AdminSignInPage client={apiClient} />} />
        <Route path="/admin" element={<AdminHomePage client={apiClient} />} />
        <Route path="/admin/people" element={<PeopleListPage client={apiClient} />} />
        <Route path="/admin/people/new" element={<PersonNewPage client={apiClient} />} />
        <Route path="/admin/people/:personId" element={<PersonDetailPage client={apiClient} />} />
        <Route
          path="/admin/compliance-records"
          element={<ComplianceRecordsListPage client={apiClient} />}
        />
        <Route
          path="/admin/compliance-records/:recordId"
          element={<ComplianceRecordDetailPage client={apiClient} />}
        />
        <Route path="/admin/worker-jobs" element={<WorkerJobsPage client={apiClient} />} />
        <Route path="/admin/exports" element={<AdminExportsPage client={apiClient} />} />
        <Route path="/admin/users" element={<AdminUsersListPage client={apiClient} />} />
        <Route
          path="/admin/users/:adminUserId"
          element={<AdminUserDetailPage client={apiClient} />}
        />
        <Route
          path="/admin/compliance-obligations"
          element={<ComplianceRequirementsListPage client={apiClient} />}
        />
        <Route
          path="/admin/compliance-obligations/:requirementId"
          element={<ComplianceRequirementDetailPage client={apiClient} />}
        />
        {/* Old paths predate the obligations rename — keep them resolving so
            existing bookmarks and back-button journeys don't 404. The backend
            API path stays `/compliance-requirements`. */}
        <Route
          path="/admin/compliance-requirements"
          element={<Navigate to="/admin/compliance-obligations" replace />}
        />
        <Route
          path="/admin/compliance-requirements/:requirementId"
          element={<RedirectToObligation />}
        />
        <Route path="/admin/documents" element={<AdminDocumentsPage client={apiClient} />} />
        <Route
          path="/admin/*"
          element={
            <PlaceholderPage
              title="Admin dashboard"
              summary="More admin surfaces land in subsequent M13 slices."
            />
          }
        />

        {/* Landing — fresh visitors with no session land on the volunteer
            sign-in. The "I'm an admin" link there points at /admin. */}
        <Route path="/" element={<Navigate to="/sign-in" replace />} />

        {/* Catch-all — redirect to /sign-in as the most common volunteer
            landing point. The tokenised URLs above match before this. */}
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
